'use client';

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { Session, User } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    metadata?: Record<string, string>
  ) => Promise<{ user: User | null; emailConfirmationRequired: boolean }>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Use ref so supabase client is never recreated across renders
  const supabase = useRef(createClient()).current;

  useEffect(() => {
    let mounted = true;

    // Fetch session ONCE on mount
    const initSession = async () => {
      console.count('AUTH CALL');
      const { data } = await supabase.auth.getSession();
      if (mounted) {
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setLoading(false);
      }
    };

    initSession();

    // Single auth state listener — does NOT call getSession/getUser
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      console.count('AUTH CALL');
      if (mounted) {
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      listener.subscription.unsubscribe();
    };
  }, []); // Empty dep array — runs ONCE

  // Email/Password Sign Up
  // Returns { user, emailConfirmationRequired } — never throws window access errors
  const signUp = async (
    email: string,
    password: string,
    metadata: Record<string, string> = {}
  ): Promise<{ user: User | null; emailConfirmationRequired: boolean }> => {
    // Build redirect URL safely — works in both SSR and client contexts
    const siteUrl =
      process.env.NEXT_PUBLIC_SITE_URL ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    const redirectTo = siteUrl ? `${siteUrl}/auth/callback` : undefined;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName ?? '',
          role: metadata?.role ?? 'athlete',
          avatar_url: metadata?.avatarUrl ?? '',
        },
        ...(redirectTo ? { emailRedirectTo: redirectTo } : {}),
      },
    });

    if (error) throw error;

    const signedUpUser = data.user ?? null;

    // Detect email confirmation required:
    // Supabase returns a user with no session when email confirmation is needed
    const emailConfirmationRequired =
      signedUpUser !== null && data.session === null;

    // Create profile only when we have a confirmed user ID and a live session
    if (signedUpUser?.id && data.session) {
      await createUserProfile(signedUpUser.id, {
        email: signedUpUser.email ?? email,
        fullName: metadata?.fullName ?? '',
        role: metadata?.role ?? 'athlete',
      });
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[signUp] user:', signedUpUser?.id, '| emailConfirmationRequired:', emailConfirmationRequired);
    }

    return { user: signedUpUser, emailConfirmationRequired };
  };

  // Email/Password Sign In — called once from login form
  const signIn = async (email: string, password: string) => {
    console.count('AUTH CALL');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Check if Email is Verified (uses cached user — no network call)
  const isEmailVerified = () => {
    return user?.email_confirmed_at != null;
  };

  // Get User Profile from Database
  // user_profiles table does not exist — role is stored in auth metadata
  const getUserProfile = async () => {
    if (!user) return null;
    // Return a profile-like object from auth metadata
    return {
      id: user.id,
      email: user.email ?? '',
      full_name: user.user_metadata?.full_name ?? '',
      role: user.user_metadata?.role ?? 'athlete',
    };
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ─── Internal helper: create/upsert user profile after confirmed signup ───────
async function createUserProfile(
  userId: string,
  info: { email: string; fullName: string; role: string }
): Promise<void> {
  // user_profiles table does not exist in this schema.
  // Role is stored in auth.users.raw_user_meta_data via signUp options.data.
  // If the user is a coach, also upsert into coach_profiles.
  if (info.role === 'coach') {
    const supabase = createClient();
    const { error } = await supabase.from('coach_profiles').upsert(
      {
        user_id: userId,
        name: info.fullName || 'Coach',
        email: info.email,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );
    if (error && process.env.NODE_ENV === 'development') {
      console.error('[createUserProfile] coach_profiles upsert error:', error.message);
    }
  }
}
